import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { Sidebar } from './Sidebar';
import { ReviewModeBar } from './ReviewMode';
import { SectionNav } from './SectionNav';
import { TopBar } from './TopBar';
import { SkipLink } from './SkipLink';
import { Drawer } from '../overlays/Drawer';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { useDataVersionWatcher } from '@/hooks/useDataVersionWatcher';

export interface AppShellProps {
  children: ComponentChildren;
}

// Preferencia del usuario: sidebar fijada expandida (raíl + hover si no).
// localStorage para que sobreviva sesiones; try/catch por modos privados.
const SIDEBAR_PINNED_KEY = 'sidebar:pinned';

function readSidebarPinned(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_PINNED_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell({ children }: AppShellProps) {
  // Las pantallas se ponen al día solas cuando cambian los datos del servidor.
  useDataVersionWatcher();
  const isDesktop = useBreakpoint('md'); // >= 1024
  const [path] = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(readSidebarPinned);

  const toggleSidebarPinned = () => {
    setSidebarPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_PINNED_KEY, next ? '1' : '0');
      } catch {
        // Storage no disponible — la preferencia vive solo en la sesión.
      }
      return next;
    });
  };

  useEffect(() => {
    setDrawerOpen(false);
  }, [path]);

  return (
    <div className="bg-app text-fg flex h-screen w-screen overflow-hidden">
      <SkipLink />
      {isDesktop ? (
        <Sidebar
          collapsed={!sidebarPinned}
          pinned={sidebarPinned}
          onTogglePin={toggleSidebarPinned}
        />
      ) : (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          side="left"
          size="sm"
          hideCloseButton
          unstyledBody
          // El Drawer es solo el contenedor de la sidebar móvil — su panel
          // debe matchear EXACTAMENTE el ancho/fondo del Sidebar. Sin estos
          // overrides queda una franja `bg-surface` blanca a la derecha del
          // sidebar (porque w-80 > --sidebar-w) y se ve un borde doble.
          className="bg-canvas w-[var(--sidebar-w)] border-0"
        >
          <Sidebar collapsed={false} onClose={() => setDrawerOpen(false)} />
        </Drawer>
      )}
      <div className="relative flex min-w-0 flex-1 flex-col">
        <TopBar onOpenSidebar={isDesktop ? undefined : () => setDrawerOpen(true)} />
        <SectionNav />
        <ReviewModeBar />
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-auto bg-transparent outline-none"
        >
          {children}
        </main>
      </div>
    </div>
  );
}
